import React, { createContext, useContext, useEffect } from 'react';
import { useTouchDeviceDetection } from '@/hooks/useTouchDeviceDetection';

interface TouchContextType {
  capabilities: ReturnType<typeof useTouchDeviceDetection>['capabilities'];
  viewport: ReturnType<typeof useTouchDeviceDetection>['viewport'];
  getOptimalTouchTarget: ReturnType<typeof useTouchDeviceDetection>['getOptimalTouchTarget'];
  getOptimalSpacing: ReturnType<typeof useTouchDeviceDetection>['getOptimalSpacing'];
  shouldUseNativeScrolling: ReturnType<typeof useTouchDeviceDetection>['shouldUseNativeScrolling'];
  getDropdownBehavior: ReturnType<typeof useTouchDeviceDetection>['getDropdownBehavior'];
  isTouchDevice: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  prefersTouchInteraction: boolean;
}

const TouchContext = createContext<TouchContextType | null>(null);

interface TouchProviderProps {
  children: React.ReactNode;
}

export const TouchProvider: React.FC<TouchProviderProps> = ({ children }) => {
  const touchDetection = useTouchDeviceDetection();

  // Apply adaptive CSS classes to document body
  useEffect(() => {
    const body = document.body;
    
    // Remove existing classes
    body.classList.remove(
      'touch-device',
      'coarse-pointer', 
      'fine-pointer',
      'mobile-device',
      'tablet-device',
      'desktop-device',
      'prefers-touch'
    );

    // Add current classes
    if (touchDetection.isTouchDevice) {
      body.classList.add('touch-device');
    }
    
    if (touchDetection.capabilities.hasCoarsePointer) {
      body.classList.add('coarse-pointer');
    }
    
    if (touchDetection.capabilities.hasFinePointer) {
      body.classList.add('fine-pointer');
    }
    
    if (touchDetection.isMobile) {
      body.classList.add('mobile-device');
    } else if (touchDetection.isTablet) {
      body.classList.add('tablet-device');
    } else if (touchDetection.isDesktop) {
      body.classList.add('desktop-device');
    }
    
    if (touchDetection.prefersTouchInteraction) {
      body.classList.add('prefers-touch');
    }
  }, [
    touchDetection.isTouchDevice,
    touchDetection.capabilities.hasCoarsePointer,
    touchDetection.capabilities.hasFinePointer,
    touchDetection.isMobile,
    touchDetection.isTablet,
    touchDetection.isDesktop,
    touchDetection.prefersTouchInteraction
  ]);

  return (
    <TouchContext.Provider value={touchDetection}>
      {children}
    </TouchContext.Provider>
  );
};

export const useTouchContext = () => {
  const context = useContext(TouchContext);
  if (!context) {
    throw new Error('useTouchContext must be used within a TouchProvider');
  }
  return context;
};

// Helper hook for adaptive component behavior
export const useAdaptiveComponent = () => {
  const touch = useTouchContext();
  
  return {
    // Get appropriate class names based on device capabilities
    getButtonClasses: (baseClasses: string = '') => {
      const touchClasses = touch.prefersTouchInteraction ? 'touch-target' : 'touch-target-small';
      const hoverClasses = touch.capabilities.supportsHover ? 'hover-enhanced' : '';
      return `${baseClasses} ${touchClasses} ${hoverClasses}`.trim();
    },
    
    // Get appropriate spacing
    getSpacingClasses: () => {
      if (touch.isMobile) return 'spacing-mobile gap-2';
      if (touch.isTablet) return 'spacing-tablet gap-3';
      return 'spacing-desktop gap-4';
    },
    
    // Get modal size classes
    getModalClasses: () => {
      if (touch.isMobile) return 'modal-mobile w-[95vw]';
      if (touch.isTablet) return 'modal-tablet w-[85vw]';
      return 'modal-desktop w-full max-w-2xl';
    },
    
    // Get popover classes
    getPopoverClasses: () => {
      if (touch.isMobile) return 'popover-mobile';
      if (touch.isTablet) return 'popover-tablet';
      return 'popover-desktop';
    },
    
    // Get form classes
    getFormClasses: () => {
      if (touch.prefersTouchInteraction) return 'form-touch';
      return 'form-desktop';
    },
    
    // Get optimal interaction behavior
    getInteractionBehavior: () => ({
      triggerOnHover: touch.capabilities.supportsHover && !touch.prefersTouchInteraction,
      useRippleEffect: touch.prefersTouchInteraction,
      preventDoubleClick: touch.isTouchDevice,
      enableSwipeGestures: touch.isTouchDevice && touch.isMobile,
      autoCloseOnSelect: touch.prefersTouchInteraction
    })
  };
};